import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import { NavBar } from '../components/NavBar';

type Platform = 'instagram' | 'tiktok' | 'youtube' | 'linkedin' | 'telegram' | 'facebook';

interface Topic {
  id: string;
  title: string;
  platforms: Platform[];
  language: 'uk' | 'ru' | 'pl' | 'lv' | 'en';
  scheduledDate: string | null;
  cta: string | null;
  abTest: boolean;
  status: string;
}

const STATUS_META: Record<string, { label: string; cls: string }> = {
  draft:            { label: 'Черновик',   cls: 'badge-slate' },
  script_pending:   { label: 'Генерируем', cls: 'badge-amber' },
  script_ready:     { label: 'Скрипт готов', cls: 'badge-indigo' },
  script_approved:  { label: 'Скрипт одобрен', cls: 'badge-violet' },
  video_generating: { label: 'Видео создаётся', cls: 'badge-amber' },
  video_ready:      { label: 'Видео готово', cls: 'badge-indigo' },
  video_approved:   { label: 'Видео одобрено', cls: 'badge-violet' },
  published:        { label: 'Опубликовано', cls: 'badge-green' },
};

const PLATFORM_ICON: Record<Platform, string> = {
  instagram: '📸', tiktok: '🎵', youtube: '▶️', linkedin: '💼', telegram: '✈️', facebook: '👍',
};

export default function TopicsPlanner() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [title, setTitle] = useState('');
  const [platforms, setPlatforms] = useState<Platform[]>(['instagram']);
  const [language, setLanguage] = useState<'uk' | 'ru' | 'pl' | 'lv' | 'en'>('ru');
  const [date, setDate] = useState('');
  const [abTest, setAbTest] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [globalCta, setGlobalCta] = useState('');
  const [busy, setBusy] = useState(false);

  const drafts = topics.filter((t) => t.status === 'draft');

  const load = () => api.get('/topics').then((r) => setTopics(r.data.topics)).catch(() => {});

  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [user, navigate]);

  async function addTopic(e: FormEvent) {
    e.preventDefault();
    if (!title) return;
    setBusy(true);
    try {
      await api.post('/topics', {
        title, platforms, language,
        scheduledDate: date || null,
        abTest,
      });
      setTitle(''); setDate(''); setAbTest(false);
      load();
    } finally { setBusy(false); }
  }

  async function bulkPaste() {
    const lines = bulkText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (lines.length === 0) return;
    setBusy(true);
    try {
      await api.post('/topics/bulk', {
        topics: lines.map((t) => ({ title: t, platforms, language })),
      });
      setBulkText(''); setBulkOpen(false);
      load();
    } finally { setBusy(false); }
  }

  async function applyCta() {
    if (!globalCta) return;
    setBusy(true);
    try {
      const r = await api.post('/topics/apply-global-cta', { cta: globalCta });
      alert(`CTA применён к ${r.data.updated} темам`);
      setGlobalCta('');
      load();
    } finally { setBusy(false); }
  }

  async function generateAll() {
    setBusy(true);
    try {
      const r = await api.post('/topics/generate-scripts', {});
      alert(`В очередь поставлено: ${r.data.queued}. Смотри на странице «Скрипты» — появятся через ~15-30 сек.`);
      load();
    } finally { setBusy(false); }
  }

  async function generateOne(id: string) {
    setBusy(true);
    try {
      const r = await api.post('/topics/generate-scripts', { topicIds: [id] });
      if (r.data.queued === 0) {
        alert('Тема не в статусе draft / script_ready — пересоздание заблокировано.');
      } else {
        alert(`Скрипт ставится в очередь. Появится на странице «Скрипты» через ~15-30 сек.`);
      }
      load();
    } finally { setBusy(false); }
  }

  async function removeTopic(id: string) {
    if (!confirm('Удалить тему?')) return;
    try { await api.delete(`/topics/${id}`); load(); }
    catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      alert(msg ?? 'Ошибка');
    }
  }

  const allPlatforms: Platform[] = ['instagram', 'facebook', 'tiktok', 'youtube', 'linkedin', 'telegram'];

  // Toggle a single platform on an existing topic via PATCH
  async function togglePlatform(t: Topic, p: Platform) {
    const next = t.platforms.includes(p) ? t.platforms.filter((x) => x !== p) : [...t.platforms, p];
    try {
      await api.patch(`/topics/${t.id}`, { platforms: next });
      load();
    } catch (err) {
      // silently ignore — topic might be locked in non-draft state
      console.warn('platform toggle blocked:', err);
    }
  }

  return (
    <div className="min-h-screen lg:pl-64">
      <NavBar />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6 pb-16">

        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Темы на месяц</h1>
            <p className="text-slate-500 mt-1.5">Что ты хочешь снять? Добавь темы — AI сгенерирует скрипты.</p>
          </div>
          <button
            onClick={generateAll}
            disabled={busy || drafts.length === 0}
            className="btn btn-lg btn-primary"
            title={drafts.length === 0 ? 'Добавь хотя бы одну тему' : `Сгенерировать ${drafts.length} скриптов`}
          >
            ✨ Сгенерировать скрипты · {drafts.length}
          </button>
        </header>

        {/* Add topic form */}
        <form onSubmit={addTopic} className="card-glass p-6 mb-4">
          <div className="text-sm font-semibold text-slate-700 mb-4">Новая тема</div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Например: «Как AI помогает малому бизнесу»"
            className="input mb-4"
            required
          />
          <div className="flex gap-2 flex-wrap items-center">
            {allPlatforms.map((p) => {
              const on = platforms.includes(p);
              return (
                <label key={p} className={`chip ${on ? 'chip-checked' : 'chip-unchecked'}`}>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={on}
                    onChange={(e) => setPlatforms(e.target.checked ? [...platforms, p] : platforms.filter((x) => x !== p))}
                  />
                  <span>{PLATFORM_ICON[p]}</span>
                  <span>{p}</span>
                </label>
              );
            })}
            <span className="w-px h-6 bg-slate-200 mx-1" />
            <select value={language} onChange={(e) => setLanguage(e.target.value as 'ru' | 'uk' | 'pl' | 'lv' | 'en')} className="select">
              <option value="ru">🇷🇺 ru</option>
              <option value="uk">🇺🇦 uk</option>
              <option value="pl">🇵🇱 pl</option>
              <option value="lv">🇱🇻 lv</option>
              <option value="en">🇬🇧 en</option>
            </select>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="select" />
            <label className={`chip ${abTest ? 'chip-checked' : 'chip-unchecked'}`}>
              <input type="checkbox" className="sr-only" checked={abTest} onChange={(e) => setAbTest(e.target.checked)} />
              <span>A/B тест</span>
            </label>
            <button type="submit" disabled={busy || !title} className="btn btn-md btn-primary ml-auto">
              + Добавить
            </button>
          </div>
        </form>

        {/* Bulk paste */}
        <div className="card-glass mb-4 overflow-hidden">
          <button
            onClick={() => setBulkOpen(!bulkOpen)}
            className="w-full flex items-center justify-between p-4 text-sm font-medium hover:bg-slate-50/50 transition"
          >
            <span>📥 Массовый ввод · несколько тем построчно</span>
            <span className="text-slate-400">{bulkOpen ? '▲' : '▼'}</span>
          </button>
          {bulkOpen && (
            <div className="p-4 pt-0 space-y-3">
              <textarea
                value={bulkText} onChange={(e) => setBulkText(e.target.value)}
                rows={6}
                placeholder={'Тема 1\nТема 2\nТема 3'}
                className="textarea font-mono"
              />
              <button onClick={bulkPaste} disabled={busy || !bulkText} className="btn btn-md btn-primary">
                Импортировать ({bulkText.split('\n').filter(s => s.trim()).length})
              </button>
            </div>
          )}
        </div>

        {/* Global CTA */}
        <div className="card-glass p-4 mb-6 flex flex-wrap gap-3 items-center">
          <span className="text-sm text-slate-600 shrink-0">🎯 Общий CTA</span>
          <input
            value={globalCta} onChange={(e) => setGlobalCta(e.target.value)}
            placeholder="Например: «Подписывайся, чтобы не пропустить»"
            className="input flex-1 min-w-[240px]"
          />
          <button onClick={applyCta} disabled={busy || !globalCta} className="btn btn-md btn-subtle">
            Применить ко всем без CTA
          </button>
        </div>

        {/* Topics list */}
        <section>
          <h2 className="text-lg font-semibold mb-3 text-slate-700">
            Все темы <span className="text-slate-400 font-normal">· {topics.length}</span>
          </h2>
          {topics.length === 0 ? (
            <div className="card-glass p-12 text-center">
              <div className="text-5xl mb-3">💡</div>
              <h3 className="font-semibold mb-1">Добавь свою первую тему</h3>
              <p className="text-sm text-slate-500">Введи название выше и нажми «+ Добавить». Потом одной кнопкой AI напишет скрипты для всех.</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {topics.map((t) => {
                const st = STATUS_META[t.status] ?? STATUS_META.draft!;
                return (
                  <div key={t.id} className="card-glass card-glass-hover p-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 mb-1">
                        <div className="font-semibold truncate">{t.title}</div>
                        {t.abTest && <span className="badge badge-violet">A/B</span>}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                        <span className="flex gap-1">
                          {allPlatforms.map((p) => {
                            const active = t.platforms.includes(p);
                            return (
                              <button
                                key={p}
                                onClick={() => togglePlatform(t, p)}
                                title={`${active ? 'Убрать' : 'Добавить'} ${p}`}
                                className={`text-base leading-none px-1.5 py-0.5 rounded-md transition ${
                                  active
                                    ? 'opacity-100 bg-indigo-100 hover:bg-indigo-200'
                                    : 'opacity-30 hover:opacity-70 hover:bg-slate-100'
                                }`}
                              >
                                {PLATFORM_ICON[p]}
                              </button>
                            );
                          })}
                        </span>
                        <span>🌐 {t.language}</span>
                        {t.scheduledDate && <span>📅 {t.scheduledDate}</span>}
                        {t.cta && <span className="truncate max-w-[200px]">💬 {t.cta}</span>}
                      </div>
                    </div>
                    <span className={`badge ${st.cls}`}>{st.label}</span>
                    {(t.status === 'draft' || t.status === 'script_ready') && (
                      <button
                        onClick={() => generateOne(t.id)}
                        disabled={busy}
                        className="btn btn-sm btn-primary whitespace-nowrap"
                        title="Сгенерировать скрипт только для этой темы"
                      >
                        🪄 Сгенерировать
                      </button>
                    )}
                    <button onClick={() => removeTopic(t.id)} className="text-slate-400 hover:text-rose-600 text-sm px-2" title="Удалить">
                      🗑
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
